// @ts-nocheck
import React, { useState } from "react";
import { apiFetchInit, verdictLabel } from "@/legacy/helpersModule";
import { useToast } from "@/shared/components/ui";
export function CVResultCard({ row, jobTitle, inviteUrl, recruitmentJobId }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  if (row.loading) {
    return (
      <div className="bg-white rounded-2xl border border-line shadow-sm p-8 flex items-center justify-center gap-3">
        <div className="w-8 h-8 border-2 border-line border-t-brand-600 rounded-full animate-spin shrink-0" aria-hidden />
        <span className="text-ink-600 text-sm font-medium truncate">{row.filename}</span>
      </div>
    );
  }
  if (row.status === "error") {
    return (
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm px-5 py-4 text-sm text-red-800">
        <span className="font-semibold">{row.filename}</span>
        <span className="text-red-700"> — {row.error || "Could not analyse this file."}</span>
      </div>
    );
  }
  const a = row.analysis || {};
  const initial = (a.candidateName && String(a.candidateName).trim()[0]) || "?";
  const candEmail = a.email && String(a.email).trim() ? String(a.email).trim() : "";
  const candName = a.candidateName && String(a.candidateName).trim() ? String(a.candidateName).trim() : "Candidate";
  const canInvite = Boolean(
    inviteUrl && String(inviteUrl).trim() && recruitmentJobId && candEmail,
  );
  const sendInvite = async () => {
    if (!canInvite || inviteBusy || inviteSent) return;
    setInviteBusy(true);
    try {
      const r = await fetch(
        "/api/admin/cv-analyser/send-invite",
        apiFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidateName: candName,
            email: candEmail,
            jobTitle: jobTitle && String(jobTitle).trim() ? String(jobTitle).trim() : "",
            recruitmentJobId: String(recruitmentJobId),
          }),
        }),
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.show({ tone: "danger", title: "Invite not sent", message: data.error || "Could not send invite email." });
        return;
      }
      setInviteSent(true);
      toast.show({ tone: "success", title: "Invite sent", message: `Interview invite emailed to ${candEmail}.` });
    } catch {
      toast.show({ tone: "danger", title: "Connection error", message: "Could not reach server. Please try again." });
    } finally {
      setInviteBusy(false);
    }
  };
  const inviteDisabledReason = !recruitmentJobId || !inviteUrl
    ? "Link a careers job in Job Master (Careers job — interview invite link) and save."
    : !candEmail
      ? "No email found on this CV — cannot send invite."
      : "";
  return (
    <div className="bg-white rounded-2xl border border-line shadow-sm p-4 sm:p-5">
      <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-full bg-brand-600 text-white flex items-center justify-center text-lg font-black shrink-0">{initial}</div>
          <div className="min-w-0">
            <p className="font-bold text-ink-900 truncate">{a.candidateName || "—"}</p>
            <p className="text-sm text-ink-400 truncate">{a.email || "—"}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {row.analysisMode === "pdf_vision" ? <span className="text-xs font-bold text-brand-800 bg-brand-100 px-2 py-1 rounded-full">Visual PDF</span> : null}
          {row.cached ? <span className="text-xs font-bold text-ink-400 bg-slate-100 px-2 py-1 rounded-full">Cached</span> : null}
          <span className="text-xs font-bold text-white bg-slate-700 px-3 py-1 rounded-full">{verdictLabel(a.verdict)}</span>
        </div>
      </div>
      <p className="text-sm text-ink-600 mb-3">
        {[a.currentRole, a.yearsExperience != null ? `${a.yearsExperience} yrs` : null, a.phone].filter(Boolean).join(" · ") || "—"}
      </p>
      {Number.isFinite(a.overallFitScore) ? (
        <div className="mb-4 rounded-2xl bg-gradient-to-br from-brand-50 via-white to-teal-50 border border-brand-100/80 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-4">
            <div className="flex flex-col items-center justify-center w-full sm:w-auto sm:min-w-[5.5rem] rounded-2xl bg-brand-600 text-white px-4 py-3 shadow-md shadow-brand-900/15">
              <span className="text-[10px] font-black uppercase tracking-wider text-brand-200">Fit</span>
              <span className="text-2xl sm:text-4xl font-black leading-none">{Math.round(a.overallFitScore)}</span>
              <span className="text-[10px] font-bold text-brand-200 mt-0.5">/ 100</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-black text-ink-400 uppercase mb-1.5">Match to your JD</p>
              <p className="text-sm text-ink-900 leading-snug">{a.fitSummary || "—"}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { k: "technicalScore", label: "Technical" },
                  { k: "experienceScore", label: "Experience" },
                  { k: "educationScore", label: "Education" },
                  { k: "cultureScore", label: "Culture" },
                ].map(({ k, label }) => (
                  <span key={k} className="inline-flex items-baseline gap-1 text-xs font-bold bg-white/90 border border-line/80 text-ink-600 px-2.5 py-1 rounded-full shadow-sm">
                    <span className="text-ink-400 font-semibold">{label}</span>
                    <span className="text-brand-700">{a[k] != null ? Number(a[k]).toFixed(1) : "—"}</span>
                    <span className="text-ink-400 font-medium">/5</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <p className="text-sm text-ink-900 leading-snug line-clamp-2 mb-4">{a.summary || ""}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="border-l-2 border-green-500 pl-3">
          <p className="text-xs font-black text-ink-400 uppercase mb-2">Strengths</p>
          <ul className="text-sm text-ink-600 space-y-1 list-disc list-inside">{(a.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
        <div className="border-l-2 border-amber-500 pl-3">
          <p className="text-xs font-black text-ink-400 uppercase mb-2">Gaps</p>
          <ul className="text-sm text-ink-600 space-y-1 list-disc list-inside">{(a.gaps || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(a.skills || []).slice(0, 8).map((sk) => (
          <span key={sk} className="text-xs font-medium bg-slate-100 text-ink-600 px-2 py-1 rounded-full">{sk}</span>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch gap-2 sm:gap-3 mb-2">
        <button
          type="button"
          disabled={!canInvite || inviteBusy || inviteSent}
          title={canInvite ? `Send interview invite to ${candEmail}` : inviteDisabledReason}
          onClick={sendInvite}
          className={`text-xs font-bold px-4 py-2 rounded-control border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 w-full sm:w-auto text-center ${
            inviteSent
              ? "border-green-600 text-green-800 bg-green-50 cursor-default"
              : canInvite
                ? "border-teal-600 text-teal-800 bg-teal-50 hover:bg-teal-100"
                : "border-line text-ink-400 bg-surface-alt cursor-not-allowed"
          }`}
        >
          {inviteBusy ? "Sending…" : inviteSent ? "Invite sent ✓" : "Send invite"}
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-bold text-brand-600 hover:text-brand-800 w-full sm:w-auto text-left sm:text-center py-2 sm:py-0">
          {open ? "Hide details" : "Show details"}
        </button>
      </div>
      {open ? (
        <div className="mt-3 pt-3 border-t border-line text-sm text-ink-600 space-y-2">
          <div>
            <p className="text-xs font-black text-ink-400 uppercase mb-1">Education</p>
            <ul className="list-disc list-inside space-y-0.5">{(a.education || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
          {(a.redFlags || []).length > 0 ? (
            <div>
              <p className="text-xs font-black text-ink-400 uppercase mb-1">Red flags</p>
              <ul className="list-disc list-inside text-amber-900 space-y-0.5">{a.redFlags.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
